// Full-screen modal shell. Renders via React portal at document.body so the
// overlay sits above the host-page content regardless of where the widget is
// mounted. Three ways to close: backdrop click, the × button, or ESC.
//
// Designed for the post-mount-point pivot: instead of pushing host-page
// content down with an inline widget, every trigger element on the page
// opens this modal which contains the booking flow.

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from '../styles/widget.module.css';
import Close from '../assets/icons/Close';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Optional label for accessibility (announced when the modal opens). */
  ariaLabel?: string;
}

export function Modal({ isOpen, onClose, children, ariaLabel = 'Book a fitting' }: ModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  // Stable ref to onClose so the effect below isn't re-keyed when the
  // parent passes a new function identity (the Widget re-creates
  // handleCloseModal on every render). Without this, every keystroke
  // in an input triggers re-mount + focus-steal, kicking the cursor
  // out of whatever field the user is typing in.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Body-scroll lock + ESC listener + initial focus — runs ONLY on the
  // open transition, not on every parent re-render. Dependency is only
  // `isOpen`; onClose comes from the ref above.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);

    // Focus the modal container on open so screen readers announce the
    // dialog. tabIndex={-1} makes it focusable. Only steal focus on the
    // open transition — never on subsequent re-renders.
    requestAnimationFrame(() => contentRef.current?.focus());

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onClick={onClose}
    >
      <div
        className={styles.modalContent}
        ref={contentRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.modalClose}
          aria-label="Close booking widget"
          onClick={onClose}
        >
          <Close />
        </button>
        {children}
      </div>
    </div>,
    document.body,
  );
}
